"use client";

/**
 * V2 Landing Page Public View
 * Renders the published V2 landing page with all components.
 * Wrapped by PublicPageProvider for context and store initialization.
 */

import type React from "react";
import { Fragment, useEffect } from "react";
import type { EventLandingPagePublicRow } from "../../_logic/types/db-types";
import type { PublicPageInitialData } from "../../_logic/types/public-page-data";
import type { PageComponent } from "../../_types";
import type { ViewerIdentity } from "../../_logic/actions/_shared/get-invited-page-access";
import type { ScheduleBannerProps } from "../../_logic/actions/_shared/schedule-banner";
import { isModalScrollLocked } from "@/fetch-ui/FetchModal";
import { getStickyHeaderOffset } from "../../_logic/utils/scroll-utils";
import { PublicPageProvider } from "./PublicPageProvider";
import { PublicComponentRenderer } from "./PublicComponentRenderer";
import { ResortMapLink } from "./ResortMapLink";
import { getResortMapForLandingSlug, resortMapSubpageHref } from "@/features/resort-map/_logic/registry";
import { ScheduleLiveBanner } from "./ScheduleLiveBanner";

interface V2LandingPagePublicViewProps {
  page: EventLandingPagePublicRow;
  components: PageComponent[];
  /** Event-level IANA timezone (hero, activities, schedule, .ics). NULL = floating time. */
  eventTimeZone: string | null;
  /** Schedule data-fetch + live-banner props, resolved server-side (see `resolveScheduleBannerProps`). */
  scheduleBanner: ScheduleBannerProps;
  /** Top-level (root) page slug — passed through to context so subpages can link to the top-level page. */
  rootSlug?: string;
  /** Whether the current user is an organizer (admin/owner/nowadays admin) */
  isOrganizer?: boolean;
  /** Server-resolved viewer identity. Null when the visitor isn't signed in. */
  viewer?: ViewerIdentity | null;
  /** Opportunistic FK to nowadays.attendees if the viewer's email matched the event roster. */
  attendeeId?: number | null;
  /** Whether the viewer may preview `test_publish` activities — gates their visibility. */
  canPreviewTestPublish?: boolean;
  /** Server-fetched initial data for the interactive sections; seeds the provider tree. */
  initialData?: PublicPageInitialData | null;
}

/**
 * Public view that renders all V2 components with anchor scroll support
 *
 * @param props - See V2LandingPagePublicViewProps.
 * @returns The page render with anchor-scroll handling.
 */
export function V2LandingPagePublicView({
  page,
  components,
  eventTimeZone,
  scheduleBanner,
  rootSlug,
  isOrganizer = false,
  viewer = null,
  attendeeId = null,
  canPreviewTestPublish = false,
  initialData = null,
}: V2LandingPagePublicViewProps): React.ReactElement {
  // Effects — anchor scroll on load (same pattern as V1)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.slice(1);
    const scrollTo = (): void => {
      // While a modal holds the scroll lock the body is pinned (`position: fixed`), so
      // `window.scrollY` reads 0 and a retry would compute a bogus target that lands the
      // visitor at the top of the page once the modal closes. Skip until it's released.
      if (isModalScrollLocked()) return;
      const el = document.getElementById(id);
      if (!el) return;
      // Offset by whatever sticky chrome (navbar + live banner) is present so the target isn't
      // hidden behind it — getStickyHeaderOffset reads each bar's published height (0 when absent).
      const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
      window.scrollTo({ top, behavior: "smooth" });
    };

    scrollTo();
    // Retry — layout shifts from collapsible sections / lazy images may move the target after
    // the initial scroll. Bail out as soon as the user interacts so the retries don't yank them
    // back while they're trying to scroll. `click`/`pointerdown` are included so opening a modal
    // (e.g. a signup card) disarms the retries — otherwise a pending retry fires after the modal
    // closes and yanks the page to the top.
    const timers = [300, 800, 1500, 3000].map((d) => setTimeout(scrollTo, d));

    const cancelRetries = (): void => {
      timers.forEach(clearTimeout);
    };
    const interactionOptions: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener("wheel", cancelRetries, interactionOptions);
    window.addEventListener("touchstart", cancelRetries, interactionOptions);
    window.addEventListener("keydown", cancelRetries, interactionOptions);
    window.addEventListener("click", cancelRetries, interactionOptions);
    window.addEventListener("pointerdown", cancelRetries, interactionOptions);

    return () => {
      cancelRetries();
      window.removeEventListener("wheel", cancelRetries);
      window.removeEventListener("touchstart", cancelRetries);
      window.removeEventListener("keydown", cancelRetries);
      window.removeEventListener("click", cancelRetries);
      window.removeEventListener("pointerdown", cancelRetries);
    };
  }, []);

  // A registered resort map gets one small link on the main page, right under the hero (or
  // ahead of the first section when there's no hero). Subpages don't repeat it.
  const landingSlug = rootSlug ?? page.slug;
  const resortMap = page.parent_page_id == null ? getResortMapForLandingSlug(landingSlug) : null;
  const resortMapLinkAfterId = resortMap
    ? (components.find((c) => c.type === "hero") ?? components.find((c) => c.type === "navbar"))?.id
    : undefined;

  // Render
  return (
    <PublicPageProvider
      page={page}
      components={components}
      eventTimeZone={eventTimeZone}
      scheduleBanner={scheduleBanner}
      rootSlug={rootSlug}
      isOrganizer={isOrganizer}
      viewer={viewer}
      attendeeId={attendeeId}
      canPreviewTestPublish={canPreviewTestPublish}
      initialData={initialData}
    >
      <ScheduleLiveBanner />
      {/* `overflow-x-clip` caps sideways overflow so a section/floating widget can't expose blank
          horizontal whitespace on mobile. `clip` (not `hidden`) avoids creating a scroll container,
          so sticky navbar/banner behavior is preserved. */}
      <div className="min-h-screen w-full max-w-full overflow-x-clip bg-white">
        {components.map((component) => (
          <Fragment key={component.id}>
            <PublicComponentRenderer component={component} components={components} />
            {resortMap && component.id === resortMapLinkAfterId && (
              <ResortMapLink
                href={resortMapSubpageHref(landingSlug)}
                label={`Interactive ${resortMap.navLabel.toLowerCase()}`}
              />
            )}
          </Fragment>
        ))}
      </div>
    </PublicPageProvider>
  );
}
